import { useState, useEffect, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { ToastPortal } from '~/components';
import { Toast, ToastContext, ToastContextValue } from '~/context';
import { ToastPosition } from '~/types';

interface ToastProviderProps {
  children: ReactNode;
  defaultPosition?: ToastPosition;
}

export function ToastProvider({
  children,
  defaultPosition = 'top-center',
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const div = document.createElement('div');
    document.body.appendChild(div);
    setContainer(div);
    return () => {
      document.body.removeChild(div);
    };
  }, []);

  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.autoClose !== false && typeof toast.autoClose === 'number') {
        const timer = setTimeout(() => {
          removeToast(toast.id);
        }, toast.autoClose);
        return () => clearTimeout(timer);
      }
    });
  }, [toasts]);

  const addToast = (toast: Omit<Toast, 'id'>) => {
    setToasts((prev) => [
      ...prev,
      {
        ...toast,
        id: Math.random().toString(),
        position: toast.position || defaultPosition,
      },
    ]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const value: ToastContextValue = {
    addToast,
    removeToast,
    defaultPosition,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {container &&
        ReactDOM.createPortal(
          <ToastPortal toasts={toasts} removeToast={removeToast} />,
          container,
        )}
    </ToastContext.Provider>
  );
}
